"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isNotCommunicated = isNotCommunicated;
exports.isInternalAiNote = isInternalAiNote;
exports.isDuplicateMention = isDuplicateMention;
exports.stripAiReflections = stripAiReflections;
exports.stripDatesAndMetadataFromTitle = stripDatesAndMetadataFromTitle;
/**
 * Normalize a string for fuzzy "not communicated" detection.
 * Lowercase + strip accents + trim → allows matching against normalized prefixes.
 */
function normalizeForDetection(v) {
    return v
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip accents
        .trim();
}
/**
 * Patterns that indicate "not communicated" / "not specified" / "non communiqué".
 * Each pattern is a normalized prefix (accent-stripped, lowercase).
 * When the normalized input starts with ANY of these, it's considered "not communicated".
 *
 * This covers all gender/plural/orthography variants the AI might produce:
 *   "Non spécifiée" → "non specifie" → starts with "non spec" → FILTERED
 *   "Nom non communiquée" → "nom non communique" → starts with "nom non comm" → FILTERED
 *   "Not specified" → "not specified" → starts with "not spec" → FILTERED
 *   "Não especificada" → "nao especificada" → starts with "nao espec" → FILTERED
 */
const NOT_COMMUNICATED_PREFIXES = [
    // French: "non communiqué(e)", "non spécifié(e)", "nom non communiqué/spécifié", "non renseigné(e)", "établissement non..."
    'non comm',
    'non spec',
    'nom non',
    'non rens',
    'etablissement non',
    'ecole non',
    'entreprise non',
    // English: "not communicated", "not specified", "name not..."
    'not comm',
    'not spec',
    'name not',
    // Spanish: "no comunicado(a)", "no especificado(a)"
    'no comuni',
    'no especi',
    'nombre no',
    // German: "nicht mitgeteilt", "nicht spezifiziert"
    'nicht mitg',
    'nicht spez',
    'name nicht',
    // Portuguese: "não comunicado", "não especificado"
    'nao comunic',
    'nao espec',
    'nome nao',
    // Ukrainian: "не повідомлено", "не вказано"
    'не повідомлено',
    'не вказано',
    // Arabic: "لم يتم الإبلاغ", "لم يتم التحديد"
    'لم يتم',
    // Turkish: "bilgilendirilmedi", "belirtilmedi"
    'bilgilendirilmedi',
    'belirtilmedi',
    // Romanian: "necomunicat", "nespecificat"
    'necomunicat',
    'nespecificat',
    // Catalan: "no comunicat", "no especificat"
    'no comunicat',
    'no especificat',
];
/**
 * Detects if a value is a "not communicated" / "not specified" placeholder.
 *
 * Uses normalized prefix matching instead of exact string comparison.
 * This catches all gender, plural, accent, and orthography variants
 * that an AI model might produce.
 *
 * Examples that ARE filtered:
 *   "Non spécifié", "Non spécifiée", "Non specifie", "Non specify"
 *   "Non communiqué", "Non communiquée", "Non communique"
 *   "Not specified", "Not communicated", "Name not specified"
 *   "No especificado", "No especificada", "Nombre no comunicado"
 *   "Nicht spezifiziert", "Nicht mitgeteilt"
 *   "Não especificado", "Não comunicado"
 *   etc.
 *
 * Examples that are NOT filtered (real values):
 *   "Paris", "12345", "true", "Nantes"
 */
function isNotCommunicated(v) {
    const normalized = normalizeForDetection(v);
    return NOT_COMMUNICATED_PREFIXES.some(prefix => normalized.startsWith(prefix));
}
/**
 * Patterns in mentions that indicate internal AI notes, verification requests,
 * or uncertainty memos rather than actual candidate mentions.
 * Examples: "mois à préciser", "information à confirmer", "période approximative", "à vérifier".
 */
const AI_UNCERTAINTY_PATTERNS = [
    'a preciser',
    'a confirmer',
    'a verifier',
    'a determiner',
    'a definir',
    'information a',
    'periode approximative',
    'date approximative',
    'non renseigne',
    'non specifie',
    'non communique',
    'not specified',
    'to be confirmed',
    'to confirm',
    'to be specified',
];
/**
 * Detects if a mention string is an internal AI note rather than a real candidate CV mention.
 */
function isInternalAiNote(v) {
    if (!v || isNotCommunicated(v))
        return true;
    const normalized = normalizeForDetection(v);
    return AI_UNCERTAINTY_PATTERNS.some(pattern => normalized.includes(pattern));
}
/**
 * Patterns indicating a mention relates to driver's license.
 */
const DRIVER_LICENSE_PATTERNS = [
    'permis',
    'driver license',
    'driving license',
    'licencia de conducir',
    'fuehrerschein',
    'cnh',
    'vodiy',
];
/**
 * Detects if a mention duplicates information already present in personalInfo
 * (e.g. driver license when already in personalInfo, or location).
 */
function isDuplicateMention(mention, context) {
    if (!mention)
        return true;
    const normalizedMention = normalizeForDetection(mention);
    // If candidate already has driver license recorded, filter redundant mentions like "Permis B", "Permis", etc.
    if ((context === null || context === void 0 ? void 0 : context.hasDriverLicense) || (context === null || context === void 0 ? void 0 : context.driverLicenseTypes)) {
        if (DRIVER_LICENSE_PATTERNS.some(pattern => normalizedMention.includes(pattern))) {
            return true;
        }
    }
    // If mention just repeats the city or location (e.g. "Paris", "Nantes")
    if (context === null || context === void 0 ? void 0 : context.location) {
        const normalizedLoc = normalizeForDetection(context.location);
        if (normalizedLoc.length > 2 && normalizedMention === normalizedLoc) {
            return true;
        }
    }
    return false;
}
/**
 * Phrases that indicate an internal AI verification memo/reflection inside a text.
 */
const AI_NOTE_PHRASES = [
    'à préciser',
    'a preciser',
    'à confirmer',
    'a confirmer',
    'à vérifier',
    'a verifier',
    'période approximative',
    'periode approximative',
    'non renseigné',
    'non renseigne',
    'non spécifié',
    'non specifie',
    'to be confirmed',
    'to confirm',
];
/**
 * Prefixes often introducing trailing uncertainty memos.
 */
const TRAILING_NOTE_PREFIXES = [
    'mois à préciser',
    'mois a preciser',
    'date à préciser',
    'date a preciser',
    'période à préciser',
    'periode a preciser',
    'information à confirmer',
    'information a confirmer',
    'information à préciser',
    'information a preciser',
    'information à vérifier',
    'information a verifier',
    'date à vérifier',
    'date a verifier',
    'période approximative',
    'periode approximative',
    'nom de l\'établissement à préciser',
    'nom de l\'ecole à préciser',
    'nom de l\'entreprise à préciser',
    'à préciser',
    'a preciser',
    'à confirmer',
    'a confirmer',
    'à vérifier',
    'a verifier',
];
/**
 * Strips internal AI reflections, editorial comments, or verification notes from any text field
 * (e.g. missions, summary, degree, school name).
 * Implemented using linear parsing to guarantee O(N) execution and avoid ReDoS vulnerabilities.
 */
function stripAiReflections(text) {
    if (typeof text !== 'string' || text.length === 0)
        return '';
    if (isNotCommunicated(text))
        return '';
    // Limit maximum examined length to prevent ReDoS / excessive iteration on malicious payload
    const maxLen = Math.min(text.length, 50000);
    const input = text.slice(0, maxLen);
    const OPEN_TO_CLOSE = { '(': ')', '[': ']', '{': '}' };
    const parts = [];
    let cursor = 0;
    while (cursor < maxLen) {
        const ch = input[cursor];
        const closeChar = OPEN_TO_CLOSE[ch];
        if (closeChar) {
            const closeIdx = input.indexOf(closeChar, cursor + 1);
            if (closeIdx !== -1 && closeIdx - cursor <= 200) {
                const inner = input.slice(cursor + 1, closeIdx).toLowerCase();
                const hasAiNote = AI_NOTE_PHRASES.some(phrase => inner.includes(phrase));
                if (hasAiNote) {
                    parts.push(' ');
                    cursor = closeIdx + 1;
                    continue;
                }
            }
        }
        parts.push(ch);
        cursor++;
    }
    let result = parts.join('');
    // 2. Remove trailing uncertainty notes like " - travail le week-end, période approximative"
    for (const phrase of TRAILING_NOTE_PREFIXES) {
        const idx = result.toLowerCase().lastIndexOf(phrase);
        if (idx !== -1) {
            const before = result.slice(0, idx);
            const after = result.slice(idx + phrase.length).trim();
            if (after === '' || after === '.' || after === ')' || after === ']') {
                result = before;
            }
        }
    }
    // 3. Clean up whitespace and punctuation linearly
    const words = result.split(/\s+/).filter(Boolean);
    let cleaned = words.join(' ');
    // Trim leading/trailing punctuation chars
    const PUNCT = new Set([',', ';', ':', '-', '–', '—', '.']);
    let start = 0;
    while (start < cleaned.length && (PUNCT.has(cleaned[start]) || cleaned[start] === ' ')) {
        start++;
    }
    let end = cleaned.length;
    while (end > start && (PUNCT.has(cleaned[end - 1]) || cleaned[end - 1] === ' ')) {
        end--;
    }
    return cleaned.slice(start, end).trim();
}
/**
 * Strips dates, date ranges (e.g. "2020-2022", "(09/2021)", "depuis 2019"),
 * or extraneous company/location mentions from a job title or degree name
 * when the AI mistakenly embeds them into the title field.
 */
function stripDatesAndMetadataFromTitle(title) {
    if (!title || typeof title !== 'string')
        return '';
    let result = title;
    // 1. Remove date ranges like "(2020 - 2022)", "(01/2020 - 05/2022)", "2018-2021", "(2020/2022)"
    result = result.replace(/\(?\b(?:(?:\d{1,2}[\/\-.])?(?:19\d\d|20\d\d))\s*[-–—/àa]\s*(?:actuel|aujourd'hui|présent|present|en cours|(?:\d{1,2}[\/\-.])?(?:19\d\d|20\d\d))\)?/gi, '');
    // 2. Remove single years or month/year in parentheses like "(2021)", "(09/2020)"
    result = result.replace(/\(\s*(?:\d{1,2}[\/\-.])?\b(?:19\d\d|20\d\d)\b\s*\)/gi, '');
    // 3. Remove "depuis 2020", "since 2020", "en 2020"
    result = result.replace(/\b(?:depuis|since|from|en)\s+(?:(?:\d{1,2}[\/\-.])?(?:19\d\d|20\d\d))\b/gi, '');
    // 4. Remove trailing year or date at the end of the title (e.g. "Technicien de maintenance 2022")
    result = result.replace(/\s*[-–—/]?\s*\b(?:(?:\d{1,2}[\/\-.])?(?:19\d\d|20\d\d))\b\s*$/g, '');
    // 5. Clean punctuation and spaces
    const words = result.split(/\s+/).filter(Boolean);
    let cleaned = words.join(' ');
    const PUNCT = new Set([',', ';', ':', '-', '–', '—', '.', '(', ')']);
    let start = 0;
    while (start < cleaned.length && (PUNCT.has(cleaned[start]) || cleaned[start] === ' ')) {
        start++;
    }
    let end = cleaned.length;
    while (end > start && (PUNCT.has(cleaned[end - 1]) || cleaned[end - 1] === ' ')) {
        end--;
    }
    return cleaned.slice(start, end).trim();
}
